import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card, ConfigProvider, DatePicker, Tabs } from 'antd';
import { Container } from '../../components/content';
import GenericTable from '../../components/tables/GenericTable';
import Pagination from '../../components/layout/Pagination';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import agentService from '../../api/agent-api';
import styles from '../../styles/viewmedic.module.scss';
import dayjs from 'dayjs';
import {
    PAYOUT_STATUSES,
    resolvePayoutStatusKey,
    formatPayoutStatusAsPill,
    payoutStatusLabel,
} from '../../utils/formatPayoutStatus';
import BulkMarkPaidConfirmModal from '../../components/modals/BulkMarkPaidConfirmModal';

const { RangePicker } = DatePicker;

// Orders where the agent's code was used but the buyer had no mentor referral never
// earn a commission (see order-pricing.service.ts::resolveReferralInfo) — shown with
// this distinct grey note instead of formatPayoutStatus.js's shared pill (which would
// otherwise misleadingly read "Čeka realizaciju" for any such order that's been delivered).
const NO_MENTOR_LABEL = 'Bez preporuke mentora';
const renderCommissionStatus = (row) =>
    row.commissionEligible ? (
        formatPayoutStatusAsPill(row)
    ) : (
        <span
            style={{
                padding: '4px 12px',
                borderRadius: '16px',
                fontSize: '14px',
                fontWeight: '500',
                display: 'inline-block',
                backgroundColor: '#F2F4F7',
                color: '#344054',
            }}
        >
            {NO_MENTOR_LABEL}
        </span>
    );

// The purchase table is split per payout state instead of filtered through a
// dropdown, matching the mentor detail page's pattern. 'pending' on the API
// means "not paid yet", so undelivered orders live in that tab too — their
// pill still reads "Nije isporučeno".
const payoutTabItems = [
    { key: 'pending', label: 'Čeka realizaciju' },
    { key: 'paid', label: 'Realizovano' },
];

const summaryColumns = [
    {
        key: 'fullName',
        label: 'Agent',
        render: (val) => <span>{val ?? '-'}</span>,
    },
    {
        key: 'linkedMentorName',
        label: 'Povezani mentor',
        render: (val) => <span>{val ?? '-'}</span>,
    },
    // Commission-percentage pill disabled (2026-10-13): hidden from the agent detail summary per request.
    // {
    //     key: 'commissionPercent',
    //     label: 'Provizija',
    //     render: (val) => (
    //         <span
    //             style={{
    //                 padding: '4px 12px',
    //                 borderRadius: '16px',
    //                 fontSize: '14px',
    //                 fontWeight: '500',
    //                 display: 'inline-block',
    //                 backgroundColor: '#ECFDF3',
    //                 color: '#027A48',
    //             }}
    //         >
    //             {`Provizija ${val ?? 0}%`}
    //         </span>
    //     ),
    // },
    {
        key: 'usersCount',
        label: 'Ukupan broj korisnika',
        render: (val) => <span>{val ?? 0}</span>,
    },
    {
        key: 'ordersCount',
        label: 'Ukupan broj porudžbina',
        render: (val) => <span>{val ?? 0}</span>,
    },
    {
        key: 'ordersTotal',
        label: 'Ukupna vrednost porudžbina',
        render: (val) => <span>{formatPriceWithCurrency(val || 0)}</span>,
    },
];

const ViewAgent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [agentDetails, setAgentDetails] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);

    const [orders, setOrders] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [ordersLoading, setOrdersLoading] = useState(false);

    const [filter, setFilter] = useState({
        createdFrom: null,
        createdTo: null,
    });
    const [activePayoutTab, setActivePayoutTab] = useState(
        searchParams.get('tab') === 'paid' ? 'paid' : 'pending'
    );
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkUpdating, setBulkUpdating] = useState(false);
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

    // A row can only be marked (individually or in bulk) if it actually has a ledger
    // entry and that entry is still pending — mirrors the mentor detail page exactly.
    const isRowMarkable = (row) =>
        !!row?.payoutEntryId && resolvePayoutStatusKey(row) === 'pending';

    const handleFetchDetails = async () => {
        setSummaryLoading(true);
        try {
            const response = await agentService.getDetails(id);

            if (!response.success && response.success !== undefined) {
                toast.error(response.message || 'Greška pri učitavanju podataka agenta');
                return;
            }

            const data = response.data || response;
            setAgentDetails(data);
        } catch (error) {
            console.error('Error fetching agent details:', error);
            toast.error('Greška pri učitavanju podataka agenta');
        } finally {
            setSummaryLoading(false);
        }
    };

    const handleFetchOrders = async () => {
        setOrdersLoading(true);
        try {
            const { data, success, message } = await agentService.getOrders(id, {
                page: currentPage,
                limit: 10,
                createdFrom: filter.createdFrom || undefined,
                createdTo: filter.createdTo || undefined,
                payoutStatus: activePayoutTab,
            });

            if (!success) {
                toast.error(message || 'Greška pri učitavanju porudžbina');
                setOrders([]);
                setTotalPages(1);
                return;
            }

            setOrders(data.items || []);
            setTotalPages(data.totalPages || 1);
            setSelectedRowKeys([]);
        } catch (error) {
            console.error('Error fetching agent orders:', error);
            toast.error('Greška pri učitavanju porudžbina');
            setOrders([]);
            setTotalPages(1);
        } finally {
            setOrdersLoading(false);
        }
    };

    useEffect(() => {
        handleFetchDetails();
        // eslint-disable-next-line
    }, [id]);

    useEffect(() => {
        setSearchParams({ page: currentPage, tab: activePayoutTab }, { replace: true });
        // eslint-disable-next-line
    }, [currentPage, activePayoutTab]);

    useEffect(() => {
        handleFetchOrders();
        // eslint-disable-next-line
    }, [id, currentPage, filter.createdFrom, filter.createdTo, activePayoutTab]);

    const summaryData = agentDetails
        ? [{
            fullName: `${agentDetails.firstName || ''} ${agentDetails.lastName || ''}`.trim(),
            linkedMentorName: agentDetails.linkedMentor
                ? `${agentDetails.linkedMentor.firstName} ${agentDetails.linkedMentor.lastName}`
                : null,
            commissionPercent: agentDetails.commissionPercent,
            usersCount: agentDetails.usersCount,
            ordersTotal: agentDetails.ordersPriceSummary?.total,
            ordersCount: agentDetails.ordersPriceSummary?.count,
            ordersAverage: agentDetails.ordersPriceSummary?.average,
        }]
        : [];

    const handleDateRangeChange = (dates) => {
        setFilter((prev) => ({
            ...prev,
            createdFrom: dates?.[0] ? dates[0].startOf('day').toISOString() : null,
            createdTo: dates?.[1] ? dates[1].endOf('day').toISOString() : null,
        }));
        setCurrentPage(1);
    };

    const handlePayoutTabChange = (key) => {
        setActivePayoutTab(key);
        setCurrentPage(1);
        setSelectedRowKeys([]);
        // Otherwise the previous tab's rows linger under the new tab until the
        // fetch resolves.
        setOrders([]);
    };

    const handleMarkEntryStatus = async (row, status) => {
        const { success, message } = await agentService.markPayoutEntryStatus(
            id,
            row.payoutEntryId,
            status
        );
        if (!success) {
            toast.error(message || 'Došlo je do greške pri izmeni statusa provizije.');
            return;
        }
        toast.success('Status provizije je ažuriran.');
        handleFetchOrders();
    };

    const handleBulkMarkPaid = async () => {
        // selectedRowKeys are order _ids (GenericTable's row selection always keys
        // off row._id), but the bulk-status endpoint operates on commission-ledger
        // entry ids — map through the loaded orders to get the right ids, and only
        // ones that actually have a markable pending entry.
        const entryIds = orders
            .filter((row) => selectedRowKeys.includes(row._id) && isRowMarkable(row))
            .map((row) => row.payoutEntryId);

        if (entryIds.length === 0) {
            toast.error('Nijedna od izabranih stavki nema proviziju koja čeka realizaciju.');
            return;
        }

        setBulkUpdating(true);
        const { success, message } = await agentService.markPayoutEntriesBulk(id, entryIds, 'paid');
        setBulkUpdating(false);
        if (!success) {
            toast.error(message || 'Došlo je do greške pri izmeni statusa provizije.');
            return;
        }
        toast.success('Provizija je označena kao realizovana.');
        setSelectedRowKeys([]);
        setBulkConfirmOpen(false);
        handleFetchOrders();
    };

    const handleExportOrders = async () => {
        try {
            toast.loading('Izvoz u toku...');
            const { success, data, message } = await agentService.exportOrders(id, {
                createdFrom: filter.createdFrom || undefined,
                createdTo: filter.createdTo || undefined,
                payoutStatus: activePayoutTab,
            });
            toast.dismiss();
            if (!success) {
                toast.error(message || 'Greška pri izvozu porudžbina');
                return;
            }
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `agent-${id}-porudzbine-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Porudžbine uspešno izvezene');
        } catch (error) {
            toast.dismiss();
            console.error('Export error:', error);
            toast.error('Greška pri izvozu porudžbina');
        }
    };

    const handleViewAgentOrderDetail = (orderId) => {
        navigate(`/order/${orderId}`, { state: { from: location.pathname + location.search } });
    };

    const ordersColumns = [
        {
            key: 'customerName',
            label: 'Ime kupca',
            render: (val, row) => <span>{row?.customer?.fullName ?? '-'}</span>,
        },
        {
            key: 'commissionBase',
            label: 'Osnovica za proviziju',
            render: (val) => <span>{formatPriceWithCurrency(val || 0)}</span>,
        },
        {
            key: 'commissionAgent',
            label: 'Provizija',
            render: (val) => <span>{formatPriceWithCurrency(val || 0)}</span>,
        },
        {
            key: 'percent',
            label: 'Procenat provizije',
            render: (val) => <span>{val != null ? `${val}%` : '-'}</span>,
        },
        {
            key: 'payoutStatus',
            label: 'Status provizije',
            render: (val, row) => renderCommissionStatus(row),
        },
        {
            key: 'createdAt',
            label: 'Datum kupovine',
            render: (val) => <span>{val ? dayjs(val).format('DD.MM.YYYY.') : '-'}</span>,
        },
    ];

    const orderActions = [
        {
            key: 'go-to-order',
            label: 'Idi na porudžbinu',
            icon: <img src="/assets/icons/view-icon.svg" alt="View" />,
            onClick: (row) => handleViewAgentOrderDetail(row._id),
        },
        {
            key: 'mark-paid',
            label: 'Označi proviziju kao realizovanu',
            visible: (row) => isRowMarkable(row),
            onClick: (row) => handleMarkEntryStatus(row, 'paid'),
        },
        {
            key: 'mark-pending',
            label: 'Označi proviziju kao nerealizovanu',
            visible: (row) => !!row?.payoutEntryId && row?.payoutStatus === 'paid',
            onClick: (row) => handleMarkEntryStatus(row, 'pending'),
        },
    ];

    const legendStatuses = PAYOUT_STATUSES.filter((s) =>
        activePayoutTab === 'paid' ? s.key === 'paid' : s.key !== 'paid'
    );

    const selectedMarkableRows = orders.filter(
        (row) => selectedRowKeys.includes(row._id) && isRowMarkable(row)
    );
    const selectedMarkableTotal = selectedMarkableRows.reduce(
        (sum, row) => sum + (row.commissionAgent || 0),
        0
    );

    return (
        <Container className={styles.medic}>
            <Link to={location.state?.from || '/korisnici?tab=agenti'} className="link_btn">
                <img alt="back" src="/assets/icons/chevron.svg" />
                <span>Nazad</span>
            </Link>
            <Card className="card-gradient pt-l">
                <div className={styles.medic__content} style={{ gap: '16px' }}>

                    {/* Summary table */}
                    <div className={styles.medic__inner}>
                        <GenericTable
                            data={summaryData}
                            columns={summaryColumns}
                            grayTheme={false}
                            mobNoScroll={false}
                            loading={summaryLoading}
                        />
                    </div>

                    {/* Orders / commission payout table */}
                    <div className={styles.medic__inner}>
                        <Tabs
                            activeKey={activePayoutTab}
                            onChange={handlePayoutTabChange}
                            items={payoutTabItems}
                            className="medics_tab"
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div className={styles.payoutLegend} style={{ display: 'flex', gap: '12px' }}>
                                {legendStatuses.map((s) => (
                                    <span key={s.key} className={styles.legendItem}>
                                        <span className={`${styles.payoutDot} ${styles[s.key] || ''}`} />
                                        {s.label}
                                    </span>
                                ))}
                            </div>

                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {selectedRowKeys.length > 0 && (
                                    <Button
                                        className="btn-primary"
                                        disabled={bulkUpdating || selectedMarkableRows.length === 0}
                                        onClick={() => setBulkConfirmOpen(true)}
                                    >
                                        {bulkUpdating
                                            ? 'Ažuriranje...'
                                            : `Označi ${selectedMarkableRows.length} kao realizovano`}
                                    </Button>
                                )}
                                <ConfigProvider
                                    theme={{
                                        token: {
                                            colorPrimary: '#005844',
                                            controlOutline: 'rgba(0, 88, 68, 0.1)',
                                            colorPrimaryHover: '#004636',
                                        },
                                        components: {
                                            DatePicker: {
                                                activeBorderColor: '#005844',
                                                hoverBorderColor: '#005844',
                                            }
                                        }
                                    }}
                                >
                                    <RangePicker
                                        format="DD.MM.YYYY"
                                        onChange={handleDateRangeChange}
                                        placeholder={['Datum od', 'Datum do']}
                                        allowClear
                                    />
                                </ConfigProvider>
                                <Button
                                    className={`btn-tertiary`}
                                    onClick={handleExportOrders}
                                    icon={
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                            <path d="M8 12L3 7h3V1h4v6h3L8 12z"/>
                                            <path d="M1 14h14v2H1z"/>
                                        </svg>
                                    }
                                >
                                    Izvezi
                                </Button>
                            </div>
                        </div>
                        <GenericTable
                            data={orders}
                            columns={ordersColumns}
                            grayTheme={false}
                            mobNoScroll={false}
                            loading={ordersLoading}
                            actions={orderActions}
                            rowClassName={(row) => (row.payoutStatus === 'paid' ? 'row--paid' : '')}
                            rowSelection={
                                activePayoutTab === 'pending'
                                    ? {
                                        selectedRowKeys,
                                        onChange: setSelectedRowKeys,
                                        getCheckboxProps: (row) => ({
                                            disabled: !isRowMarkable(row),
                                            title: row.commissionEligible ? payoutStatusLabel(row) : NO_MENTOR_LABEL,
                                        }),
                                    }
                                    : null
                            }
                            emptyState={{
                                icon: '/assets/icons/no-data.svg',
                                title: activePayoutTab === 'paid'
                                    ? 'Nema realizovanih provizija'
                                    : 'Nema provizija koje čekaju realizaciju',
                            }}
                        />
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </div>
                </div>
            </Card>

            <BulkMarkPaidConfirmModal
                visible={bulkConfirmOpen}
                onClose={() => setBulkConfirmOpen(false)}
                onConfirm={handleBulkMarkPaid}
                count={selectedMarkableRows.length}
                totalAmount={selectedMarkableTotal}
                loading={bulkUpdating}
            />
        </Container>
    );
};

export default ViewAgent;
